// Shared utility functions for category operations
import moment from 'moment'

export const MAX_BATCH_SIZE = 25

export const customPostRequest = async (url, data) => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      const errorMessage =
        errorBody.message || `Server responded with status: ${response.status}`
      throw new Error(errorMessage)
    }

    return await response.json().catch(e => {
      throw new Error(`Error parsing JSON: ${e.message}`)
    })
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('NetworkError')) {
      throw new Error('Network error: Please check your internet connection.')
    }
    console.error('Error in customPostRequest:', error)
    throw error
  }
}

export const fetchUserSubscription = async (userId, apiGatewayUrl) => {
  const url = `${apiGatewayUrl}/k1/users/${userId}`
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    })
    const data = await response.json()
    console.log('this is the data ', data)

    return {
      categoryCount: data.categoryCount,
      categoryLimit: data.subscription.categoryLimit
    }
  } catch (error) {
    console.error('Error fetching user subscription:', error)
    return null
  }
}

// Import validation functions from dedicated validation file
export {
  validateGeneralCategoryData,
  validateEventsAndLocationsData,
  validateCollegeCategoryData,
  validateVacationCategoryData,
  validateCustomCategoryData,
  validateSimpleCategoryData,
  validateCategoryName,
  validateDateRange,
  validateLocation
} from './categoryValidation'

// Navigation utility functions
export const createNavigationHandlers = navigate => ({
  handleHomeClick: () => navigate('/gallery'),
  handleBackClick: () => navigate(-1),
  handleBackToTop: () => window.scrollTo(0, 0),
  handleUploadClick: () => navigate('/upload'),
  handleSubscribeClick: () => navigate('/subscribe')
})

// Asset sorting utilities
export const sortAssetsByDate = (assets, sortOrder = 'asc') => {
  return Object.values(assets).sort((a, b) => {
    const dateA = a.content?.date ? moment(a.content.date) : moment()
    const dateB = b.content?.date ? moment(b.content.date) : moment()

    return sortOrder === 'asc' ? dateA.diff(dateB) : dateB.diff(dateA)
  })
}

export const sortAssetsByDateFromKeys = (assets, sortOrder = 'asc') => {
  return Object.keys(assets).sort((a, b) => {
    const dateA = assets[a].content?.date
      ? moment(assets[a].content.date, 'YYYY-MM-DDTHH:mm:ssZ').toDate()
      : new Date(0)
    const dateB = assets[b].content?.date
      ? moment(assets[b].content.date, 'YYYY-MM-DDTHH:mm:ssZ').toDate()
      : new Date(0)

    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
  })
}

export const sortAssetsByName = (assets, sortOrder = 'asc') => {
  return Object.values(assets).sort((a, b) => {
    const nameA = (a.name || '').toLowerCase()
    const nameB = (b.name || '').toLowerCase()

    if (sortOrder === 'asc') {
      return nameA.localeCompare(nameB)
    }
    return nameB.localeCompare(nameA)
  })
}

// Asset management utilities
export const removeAssetFromCollection = (assets, assetId) => {
  const updatedAssets = { ...assets }
  delete updatedAssets[assetId]
  return updatedAssets
}

export const removeAssetFromArray = (assets, assetId) => {
  return assets.filter(asset => asset.id !== assetId)
}

export const signAssetsBatch = async (
  assets,
  userId,
  postRequestSignedUrls,
  placeholderImg,
  maxBatchSize = MAX_BATCH_SIZE
) => {
  let assetIds = Object.keys(assets)
  let signedAssets = {}

  for (let i = 0; i < assetIds.length; i += maxBatchSize) {
    const assetBatch = assetIds.slice(i, i + maxBatchSize)
    const thumbnailKeys = assetBatch
      .map(id =>
        assets[id].thumbnails && assets[id].thumbnails.length > 0
          ? assets[id].thumbnails[1]
          : null
      )
      .filter(Boolean)

    const s3Uris = []
    const s3UriIdx = []

    // collect s3Uris that need signing
    assetBatch.forEach((id, idx) => {
      const asset = assets[id]
      if (!asset.fileUrl) {
        s3Uris.push(asset.s3uri)
        s3UriIdx.push(idx)
      }
    })

    const [signedThumbnailUrls, signedS3Uris] = await Promise.all([
      thumbnailKeys.length > 0
        ? postRequestSignedUrls({ userId, payload: thumbnailKeys })
        : [],
      s3Uris.length > 0
        ? postRequestSignedUrls({ userId, payload: s3Uris })
        : []
    ])

    assetBatch.forEach((id, idx) => {
      // Use the mapping index for fileUrl updates
      const s3UriSignedIndex = s3UriIdx.indexOf(idx)
      const signedFileUrl =
        s3UriSignedIndex !== -1 && signedS3Uris[s3UriSignedIndex]
          ? signedS3Uris[s3UriSignedIndex]
          : assets[id].fileUrl

      assets[id].thumbnailUrl = signedThumbnailUrls[idx] || placeholderImg
      assets[id].fileUrl = signedFileUrl
      signedAssets[id] = assets[id]
    })
  }

  return signedAssets
}

export const formatAssetDate = (date, format = 'DD MMMM YYYY') => {
  if (!date) return 'Date not available'
  return moment(date).format(format)
}

// Modal state helpers
export const createModalProps = (
  dispatch,
  modalActive,
  assets,
  assetId,
  modalStatus = 'Image'
) => ({
  dispatch,
  state: {
    modalActive: !modalActive,
    publishedAssets: assets,
    modalStatus
  },
  payload: {
    assetId
  }
})

// Error handling utilities
export const handleAsyncError = (error, context = 'Operation') => {
  console.error(`Error in ${context}:`, error)

  // Return user-friendly error messages
  if (error.name === 'TypeError' && error.message.includes('NetworkError')) {
    return 'Network error: Please check your internet connection.'
  }

  if (error.message.includes('Failed to fetch')) {
    return 'Unable to connect to server. Please try again later.'
  }

  return error.message || `${context} failed. Please try again.`
}
